
// src/pages/ProfileGate/ProfileGateRunner.jsx

import React, { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { useProfileGateDraft } from "../../hooks/useProfileGateDraft";

import {
  getValidatedProfileGateV2ResumePath,
  saveProfileGateResumeFromPath,
} from "../../lib/onboarding/profileGateResumeProgress";

import WelcomeStep from "./steps/WelcomeStep";
import GetStartedStep from "./steps/GetStartedStep";
import FocusStep from "./steps/FocusStep";
import BusinessBasicsStep from "./steps/BusinessBasicsStep";
import FinancialSetupStep from "./steps/FinancialSetupStep";
import ProfileSummaryStep from "./steps/ProfileSummaryStep";
import LearningPlanStep from "./steps/LearningPlanStep";
import OrientationStep from "./steps/OrientationStep";
import OnboardingCompleteStep from "./steps/OnboardingCompleteStep";
import ConsentNoticesStep from "./steps/ConsentNoticesStep";

// =========================================================
// ROUTE TRACKING
// =========================================================

function ProfileGateRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    saveProfileGateResumeFromPath(location.pathname);
  }, [location.pathname]);

  return null;
}

// =========================================================
// ENTRY / SMART RESUME
// =========================================================

function GateIndexRedirect() {
  const { draft, loading } = useProfileGateDraft();

  if (loading) return null;

  return (
    <Navigate
      replace
      to={getValidatedProfileGateV2ResumePath(draft)}
    />
  );
}

// =========================================================
// LEGACY REDIRECT
// =========================================================

function LegacyRedirect({ to }) {
  return <Navigate replace to={to} />;
}

// =========================================================
// ROUTER
// =========================================================

export default function ProfileGateRunner() {
  return (
    <>
      <ProfileGateRouteTracker />

      <Routes>
        <Route
          index
          element={<GateIndexRedirect />}
        />

        {/* CURRENT ONBOARDING */}

        <Route
          path="welcome"
          element={<WelcomeStep />}
        />

        <Route
          path="get-started"
          element={<GetStartedStep />}
        />

        <Route
          path="focus"
          element={<FocusStep />}
        />

        <Route
          path="business-basics"
          element={<BusinessBasicsStep />}
        />

        <Route
          path="financial-setup"
          element={<FinancialSetupStep />}
        />

        <Route
          path="summary"
          element={<ProfileSummaryStep />}
        />

        <Route
          path="learning-plan"
          element={<LearningPlanStep />}
        />

        <Route
          path="orientation"
          element={<OrientationStep />}
        />

        <Route
          path="onboarding-complete"
          element={<OnboardingCompleteStep />}
        />

        <Route
          path="consent-notices"
          element={<ConsentNoticesStep />}
        />

        <Route
  path="notifications"
  element={
    <LegacyRedirect to="/profile-gate/consent-notices" />
  }
/>

        {/* LEGACY PERSONAL DETAILS */}

        <Route
          path="full-name"
          element={
            <LegacyRedirect to="/profile-gate/get-started" />
          }
        />

        <Route
          path="phone"
          element={
            <LegacyRedirect to="/profile-gate/get-started" />
          }
        />

        <Route
          path="personal-congrats"
          element={
            <LegacyRedirect to="/profile-gate/get-started" />
          }
        />

        {/* LEGACY FOCUS ROUTES */}

        <Route
          path="app-reason"
          element={
            <LegacyRedirect to="/profile-gate/get-started" />
          }
        />

        <Route
          path="priorities"
          element={
            <LegacyRedirect to="/profile-gate/focus" />
          }
        />

        <Route
          path="priorities-complete"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="financial-challenge"
          element={
            <LegacyRedirect to="/profile-gate/focus" />
          }
        />

        {/* LEGACY BUSINESS ROUTES */}

        <Route
          path="business-profile"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="business-name"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="legal-nature"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="registration-number"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="industry"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="address"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="business-size"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="business-age"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="business-stage"
          element={
            <LegacyRedirect to="/profile-gate/business-basics" />
          }
        />

        <Route
          path="business-basics-complete"
          element={
            <LegacyRedirect to="/profile-gate/financial-setup" />
          }
        />

        <Route
          path="business-stage-complete"
          element={
            <LegacyRedirect to="/profile-gate/financial-setup" />
          }
        />

        {/* LEGACY FINANCIAL ROUTES */}

        <Route
          path="revenue"
          element={
            <LegacyRedirect to="/profile-gate/financial-setup" />
          }
        />

        <Route
          path="financial-setup-complete"
          element={
            <LegacyRedirect to="/profile-gate/summary" />
          }
        />

        <Route
          path="business-congrats"
          element={
            <LegacyRedirect to="/profile-gate/summary" />
          }
        />

        {/* LEGACY SUMMARY ROUTES */}

        <Route
          path="confidence"
          element={
            <LegacyRedirect to="/profile-gate/summary" />
          }
        />

        <Route
          path="confidence-complete"
          element={
            <LegacyRedirect to="/profile-gate/summary" />
          }
        />

        <Route
          path="review"
          element={
            <LegacyRedirect to="/profile-gate/summary" />
          }
        />

        {/* LEGACY FINAL ROUTES */}

        <Route
          path="gift"
          element={
            <LegacyRedirect to="/profile-gate/onboarding-complete" />
          }
        />

        <Route
          path="intro-step"
          element={
            <LegacyRedirect to="/profile-gate/onboarding-complete" />
          }
        />

        <Route
          path="intro-video"
          element={
            <LegacyRedirect to="/profile-gate/onboarding-complete" />
          }
        />

        {/* LEGACY CONSENT ROUTES */}

        <Route
          path="consent"
          element={
            <LegacyRedirect to="/profile-gate/consent-notices" />
          }
        />

        <Route
          path="legal-consent"
          element={
            <LegacyRedirect to="/profile-gate/consent-notices" />
          }
        />

        <Route
          path="consent-notice"
          element={
            <LegacyRedirect to="/profile-gate/consent-notices" />
          }
        />

        <Route
          path="consent-notices-step"
          element={
            <LegacyRedirect to="/profile-gate/consent-notices" />
          }
        />

        {/* UNKNOWN OR SAVED URL */}

        <Route
          path="*"
          element={<GateIndexRedirect />}
        />
      </Routes>
    </>
  );
}
